import clsx from 'clsx';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { Flex } from '../../../../../layouts/flex/Flex.js';
import { interactionsOverlay } from '../../../../core/components/interactions-overlay/InteractionsOverlay.sty.js';
import { useOverlay } from '../../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useSeriesActions } from '../../../../core/components/series-action/ChartSeriesActionContext.js';
import { MeterBarChartTooltip } from '../../../shared/components/meter-bar-tooltip/MeterBarChartTooltip.js';
import { useFormatter } from '../../../shared/context/FormatterContext.js';
import { useMeterBarLayoutSize } from '../../../shared/context/MeterBarLayoutSizeContext.js';
import { elementSize } from '../../../shared/styles/element-size.sty.js';
import { container } from '../../../shared/styles.sty.js';
import { TOOLTIP_VARIANT } from '../../constants.js';
import { useMultiMeterBarChartTooltipData } from '../../context/MultiMeterBarChartTooltipContext.js';
import { useMultiMeterBarVisibleSegmentsState } from '../../hooks/store/selectors.js';
import { useMultiMeterBarChartTooltipHandlers } from '../../hooks/useMultiMeterBarChartTooltipHandlers.js';

export const MultiMeterBarChartOverlayWithTooltip = (
  props: PropsWithChildren,
) => {
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<
    number | undefined
  >(undefined);

  const overlay = useOverlay();
  const { chartSeriesActions } = useSeriesActions();
  const { tooltipConfig } = useMultiMeterBarChartTooltipData();
  const { size } = useMeterBarLayoutSize();
  const { formatter, unit } = useFormatter();

  const visibleSegments = useMultiMeterBarVisibleSegmentsState();

  useEffect(() => {
    setHoveredSegmentIndex(undefined);
  }, [visibleSegments.length]);

  const isSharedTooltip = tooltipConfig.variant === TOOLTIP_VARIANT.Shared;
  const sharedData = {
    geometry: 'multimeterbar' as const,
    series: visibleSegments.map((item) => ({
      name: item.name ?? '',
      value: formatter(item.value, unit),
      color: item.color,
    })),
  };

  const { onClick, onPointerMove, onPointerLeave } =
    useMultiMeterBarChartTooltipHandlers(sharedData, setHoveredSegmentIndex);

  return (
    <Flex
      ref={overlay.overlayRef}
      className={clsx(container, elementSize({ size }), interactionsOverlay)}
      data-testid="overlay-wrapper"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
    >
      {props.children}

      <MeterBarChartTooltip
        variant={
          isSharedTooltip ? TOOLTIP_VARIANT.Shared : TOOLTIP_VARIANT.Single
        }
        activeItem={hoveredSegmentIndex ?? -1}
        actionsForItem={
          chartSeriesActions
            ? (item) => () => chartSeriesActions(item)
            : undefined
        }
      />
    </Flex>
  );
};
